import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { SkeletonRow } from "@/components/ui/Skeleton";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { UserType } from "@/types/db/users";
import { useTheme } from "@/hooks/useTheme";
import Modal from "@/components/Modal";
import { ModalCard } from "@/components/Modals/ModalCard";
import { RolesPicker } from "@/components/Pickers/RolesPicker";
import { supabase } from "@/lib/supabase-client";
import usePermissions from "@/hooks/usePermissions";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { userService } from "@/services/userService";
import useToast from "@/hooks/useToast";
import { Menu } from "@/components/Menu";

type UserRole = {
  role: {
    id: string;
    name: string;
    description?: string;
    meta_data?: { permissions?: Record<string, boolean | Record<string, boolean>> };
  };
};

export interface UserRolesScreenProps {
  externalPickerTrigger?: number;
}

export function UserRolesScreen({ externalPickerTrigger }: UserRolesScreenProps) {
  const { entityData, loading } = useEntityEdit<UserType>();
  const { roles: rolesPermissions } = usePermissions();
  const { colors } = useTheme();
  const toast = useToast();

  // Local state - independent of parent edit state
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [rolePickerVisible, setRolePickerVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  // Permission checks
  const canRead = rolesPermissions.read;
  const canUpdate = rolesPermissions.update;

  // Sync local state with entity data
  useEffect(() => {
    if (entityData?.user_roles) {
      setRoles(entityData.user_roles as UserRole[]);
    }
  }, [entityData?.user_roles]);

  // Open picker when external trigger changes
  useEffect(() => {
    if (externalPickerTrigger && externalPickerTrigger > 0 && canUpdate) {
      setRolePickerVisible(true);
    }
  }, [externalPickerTrigger, canUpdate]);

  // Save roles to API
  const saveRoles = useCallback(
    async (updatedRoles: UserRole[]) => {
      if (!entityData?.id) return;

      setSaving(true);
      try {
        const roleIds = updatedRoles.map((ur) => ur.role.id);
        await userService.syncUserRoles(entityData.id, roleIds);
        setRoles(updatedRoles);
        toast.showToast({ title: "Saved", message: "Roles updated", toastType: "success" });
      } catch (error: any) {
        toast.showToast({ title: "Error", message: error.message, toastType: "error" });
        // Revert to previous state
        setRoles((entityData.user_roles as UserRole[]) || []);
      } finally {
        setSaving(false);
      }
    },
    [entityData?.id, entityData?.user_roles, toast],
  );

  // Handle role removal
  const handleRemoveRole = useCallback(
    async (index: number) => {
      const updated = roles.filter((_, i) => i !== index);
      await saveRoles(updated);
    },
    [roles, saveRoles],
  );

  // Handle role picker selection
  const handlePickRoles = useCallback(
    async (roleIds: string[]) => {
      // Fetch full role details for each ID
      const { data: fetchedRoles, error } = await supabase
        .from("roles")
        .select("id, name, description, meta_data")
        .in("id", roleIds);

      if (error) {
        console.error("Failed to fetch role details:", error);
        toast.showToast({ title: "Error", message: "Failed to fetch role details", toastType: "error" });
        setRolePickerVisible(false);
        return;
      }

      // Map to user_roles structure
      const updated: UserRole[] = (fetchedRoles || []).map((role) => ({
        role: {
          id: role.id,
          name: role.name,
          description: role.description || undefined,
          meta_data: role.meta_data || undefined,
        },
      }));

      await saveRoles(updated);
      setRolePickerVisible(false);
    },
    [saveRoles, toast],
  );

  // Helper to count active permissions for a role
  const getActivePermissions = (role: UserRole["role"]) => {
    const permissions = role.meta_data?.permissions || {};
    return Object.entries(permissions)
      .filter(([_, value]) => {
        if (typeof value === "boolean") return value;
        if (typeof value === "object" && value !== null) {
          return Object.values(value).some((v) => v === true);
        }
        return false;
      })
      .map(([key]) => key);
  };

  // If no read permission, show access denied
  if (!canRead) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
        <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
          Access Denied
        </ThemedText>
        <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>You don&apos;t have permission to view roles</ThemedText>
      </ScrollView>
    );
  }

  if (loading || !entityData) {
    return (
      <View style={styles.container}>
        <View style={styles.section}>
          <SkeletonRow />
          <SkeletonRow />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} pointerEvents={saving ? "none" : "auto"}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {roles.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="account-key-outline" size={48} color={colors.textSecondary} />
            <ThemedText style={{ opacity: 0.7, marginTop: 12 }}>No roles assigned</ThemedText>
            {canUpdate && (
              <ThemedText style={{ opacity: 0.5, marginTop: 4, fontSize: 12 }}>
                Use the + button to add roles
              </ThemedText>
            )}
          </View>
        ) : (
          <View style={styles.rolesList}>
            {roles.map((userRole, index) => {
              const activePermissions = getActivePermissions(userRole.role);
              return (
                <View
                  key={userRole.role.id}
                  style={[
                    styles.roleCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <View style={styles.roleHeader}>
                    <View style={styles.roleInfo}>
                      <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                        {userRole.role.name}
                      </ThemedText>
                      {userRole.role.description && (
                        <ThemedText style={{ opacity: 0.6, marginTop: 4, fontSize: 13 }}>
                          {userRole.role.description}
                        </ThemedText>
                      )}
                    </View>
                    {canUpdate && (
                      <Menu
                        buttonStyle={{
                          alignSelf: "center",
                          borderWidth: 0,
                          backgroundColor: "transparent",
                        }}
                        hideArchive={true}
                        hideDuplicate={true}
                        hideEdit={true}
                        onDelete={() => handleRemoveRole(index)}
                      />
                    )}
                  </View>

                  {/* Permission Summary */}
                  {activePermissions.length > 0 && (
                    <View style={styles.permissionSummary}>
                      <ThemedText style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>Permissions:</ThemedText>
                      <View style={styles.permissionChips}>
                        {activePermissions.slice(0, 5).map((key) => (
                          <View key={key} style={[styles.chip, { backgroundColor: colors.primary + "20" }]}>
                            <ThemedText style={{ fontSize: 11, color: colors.primary }}>{key}</ThemedText>
                          </View>
                        ))}
                        {activePermissions.length > 5 && (
                          <View style={[styles.chip, { backgroundColor: colors.textSecondary + "20" }]}>
                            <ThemedText style={{ fontSize: 11, opacity: 0.6 }}>
                              +{activePermissions.length - 5} more
                            </ThemedText>
                          </View>
                        )}
                      </View>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}

        {!canUpdate && (
          <View style={[styles.permissionNotice, { backgroundColor: colors.attention.background }]}>
            <MaterialCommunityIcons name="information" size={16} color={colors.attention.text} />
            <ThemedText style={{ fontSize: 12, color: colors.attention.text, flex: 1 }}>
              You need &quot;roles update&quot; permission to modify role assignments.
            </ThemedText>
          </View>
        )}
      </ScrollView>

      {/* Role Picker Modal */}
      {rolePickerVisible && (
        <Modal onUnmount={() => setRolePickerVisible(false)}>
          <ModalCard>
            <RolesPicker
              selectedIds={roles.map((ur) => ur.role.id)}
              onPick={handlePickRoles}
              onCancel={() => setRolePickerVisible(false)}
            />
          </ModalCard>
        </Modal>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  section: {
    padding: 16,
    gap: 16,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
  },
  rolesList: {
    gap: 12,
  },
  roleCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  roleHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  roleInfo: {
    flex: 1,
  },
  permissionSummary: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.1)",
  },
  permissionChips: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  permissionNotice: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 8,
  },
});
